// PRE RMA — Module Gestion financière (Tableau 19, valeurs en Ariary).
// Source : SAISIE_TAB19_GESTION_FINANCIERE.html. L'utilisateur ne saisit QUE les flux de base ;
// tous les totaux, soldes théoriques et écarts sont calculés ici (jamais saisis).

export type SectionFinance = 'MEDICAMENTS' | 'FANOME' | 'EQUITE';
export const SECTIONS_FINANCE: SectionFinance[] = ['MEDICAMENTS', 'FANOME', 'EQUITE'];
export const SECTION_LABELS: Record<SectionFinance, string> = {
  MEDICAMENTS: 'I – Situation médicaments (Fanome)',
  FANOME: 'II – Situation financière : FANOME',
  EQUITE: "III – Situation financière : Fonds d'équité",
};
export const LIGNES_SECTION: Record<SectionFinance, string[]> = {
  MEDICAMENTS: ['Médicaments (Fanome)'], FANOME: ['PhaGCom', 'Dentisterie'], EQUITE: ['PhaGCom', 'Dentisterie'],
};

/** Champs de saisie par section (libellés = ceux du Tableau 19). */
export const CHAMPS_FINANCE: Record<SectionFinance, Array<{ cle: string; label: string; groupe: string; report?: boolean }>> = {
  MEDICAMENTS: [
    { cle: 'b', label: 'Valeur début du mois (1)', groupe: 'Début de mois', report: true },
    { cle: 'e1', label: 'Subvention État', groupe: 'Entrées du mois (2)' },
    { cle: 'e2', label: 'Livraison médic./consommables FANOME', groupe: 'Entrées du mois (2)' },
    { cle: 'e3', label: 'Autres (dons, prêt…)', groupe: 'Entrées du mois (2)' },
    { cle: 's1', label: 'Vente au malade', groupe: 'Sorties du mois (3)' },
    { cle: 's2', label: 'Vente aux agents communautaires', groupe: 'Sorties du mois (3)' },
    { cle: 's3', label: 'Médic. inutilisables (périmés, avariés)', groupe: 'Sorties du mois (3)' },
    { cle: 's4', label: 'Redéployés, médic. prêtés', groupe: 'Sorties du mois (3)' },
    { cle: 's5', label: 'Vol, effraction', groupe: 'Sorties du mois (3)' },
    { cle: 'ph', label: 'Valeur physique – inventaire (5)', groupe: 'Fin de mois' },
  ],
  FANOME: [
    { cle: 'b', label: 'Solde fin mois précédent (1)', groupe: 'Début de mois', report: true },
    { cle: 'r1', label: 'Vente (CSB, AC, CNSS, Voucher, autres)', groupe: 'Recettes du mois (2)' },
    { cle: 'r2', label: 'Restitution vol & détournement', groupe: 'Recettes du mois (2)' },
    { cle: 'r3', label: 'Autres recettes (redéploiement, dotations)', groupe: 'Recettes du mois (2)' },
    { cle: 'd1', label: 'Achats médicaments et consommables', groupe: 'Dépenses du mois (3)' },
    { cle: 'd2', label: "Fonds d'équité (montant à verser)", groupe: 'Dépenses du mois (3)' },
    { cle: 'd3', label: 'Autres (divers frais et fonctionnement)', groupe: 'Dépenses du mois (3)' },
    { cle: 'cn', label: 'Montant à rembourser par CNSS (5)', groupe: 'Fin de mois' },
    { cle: 'ca', label: 'Solde en caisse (6)', groupe: 'Fin de mois' },
    { cle: 'bq', label: 'Solde en banque (7)', groupe: 'Fin de mois' },
  ],
  EQUITE: [
    { cle: 'b', label: 'Solde fin mois précédent (1)', groupe: 'Début de mois', report: true },
    { cle: 'r1', label: '3/135 des recettes médicaments/consommables', groupe: 'Recettes du mois (2)' },
    { cle: 'r2', label: 'Subvention, dons, autres entrées', groupe: 'Recettes du mois (2)' },
    { cle: 'd1', label: 'Montant bons de soins (remboursement)', groupe: 'Dépenses du mois (3)' },
    { cle: 'd2', label: 'Autres à préciser en observation', groupe: 'Dépenses du mois (3)' },
    { cle: 'ca', label: 'Solde en caisse (5)', groupe: 'Fin de mois' },
    { cle: 'bq', label: 'Solde en banque (6)', groupe: 'Fin de mois' },
  ],
};

/** Champs « physiques » dont la saisie signifie que la fin de mois a été renseignée. */
export const CHAMPS_FIN_MOIS: Record<SectionFinance, string[]> = { MEDICAMENTS: ['ph'], FANOME: ['cn', 'ca', 'bq'], EQUITE: ['ca', 'bq'] };

export type SaisieFinance = Partial<Record<string, number | null>> & { ob?: string };
const n = (x: unknown) => { const v = typeof x === 'number' ? x : parseInt(String(x ?? ''), 10); return Number.isFinite(v) ? v : 0; };

export interface CalculFinance {
  debut: number; totalEntrees: number; totalSorties: number; theorique: number; physique: number; ecart: number;
  reporte: boolean;
}

/** `debutReporte` = valeur physique de fin du mois précédent (null si aucune). La saisie manuelle de « début » l'emporte. */
export function calculerFinance(section: SectionFinance, s: SaisieFinance, debutReporte: number | null = null): CalculFinance {
  const manuel = s.b !== null && s.b !== undefined;
  const debut = manuel ? n(s.b) : (debutReporte ?? 0);
  let entrees = 0, sorties = 0, physique = 0;
  if (section === 'MEDICAMENTS') {
    entrees = n(s.e1) + n(s.e2) + n(s.e3);
    sorties = n(s.s1) + n(s.s2) + n(s.s3) + n(s.s4) + n(s.s5);
    physique = n(s.ph);
  } else if (section === 'FANOME') {
    entrees = n(s.r1) + n(s.r2) + n(s.r3);
    sorties = n(s.d1) + n(s.d2) + n(s.d3);
    physique = n(s.cn) + n(s.ca) + n(s.bq);
  } else {
    entrees = n(s.r1) + n(s.r2);
    sorties = n(s.d1) + n(s.d2);
    physique = n(s.ca) + n(s.bq);
  }
  const theorique = debut + entrees - sorties;
  return { debut, totalEntrees: entrees, totalSorties: sorties, theorique, physique, ecart: physique - theorique, reporte: !manuel && debutReporte !== null };
}

/** Montant du fonds d'équité à verser = 3/135 de la vente de médicaments/consommables (arrondi à l'Ariary). */
export const montantFondsEquite = (venteMedicaments: number) => Math.round((n(venteMedicaments) * 3) / 135);

export interface ControleFinance { niveau: 'ok' | 'warn' | 'bad'; message: string }

/** Mêmes contrôles que le fichier de saisie. */
export function controlerFinance(section: SectionFinance, s: SaisieFinance, debutReporte: number | null = null): ControleFinance | null {
  const c = calculerFinance(section, s, debutReporte);
  const mouvements = CHAMPS_FINANCE[section].some((f) => f.cle !== 'b' && !CHAMPS_FIN_MOIS[section].includes(f.cle) && n(s[f.cle]) !== 0)
    || CHAMPS_FINANCE[section].some((f) => f.cle !== 'b' && n(s[f.cle]) !== 0);
  const finRenseignee = CHAMPS_FIN_MOIS[section].some((k) => s[k] !== null && s[k] !== undefined && n(s[k]) !== 0);
  const obs = (s.ob ?? '').trim();
  if (c.theorique < 0) return { niveau: 'bad', message: `Les sorties dépassent les disponibles (${c.theorique.toLocaleString('fr-FR')} Ar)` };
  if (mouvements && !finRenseignee) return { niveau: 'warn', message: "Saisissez l'inventaire / les soldes de fin de mois pour calculer l'écart" };
  if (finRenseignee && c.ecart !== 0) {
    const sg = `${c.ecart > 0 ? '+' : ''}${c.ecart.toLocaleString('fr-FR')}`;
    return obs ? { niveau: 'warn', message: `Écart de ${sg} Ar justifié en observation` } : { niveau: 'bad', message: `Écart de ${sg} Ar : expliquez-le dans Observation` };
  }
  if (finRenseignee) return { niveau: 'ok', message: 'Aucun écart' };
  return null;
}

export function validerFinance(section: SectionFinance, s: SaisieFinance): { ok: boolean; erreurs: string[] } {
  const erreurs: string[] = [];
  const cles = new Set([...CHAMPS_FINANCE[section].map((f) => f.cle), 'ob']);
  for (const [k, v] of Object.entries(s)) {
    if (!cles.has(k)) erreurs.push(`Champ inconnu : ${k}.`);
    else if (k === 'ob') { if (typeof v === 'string' && v.length > 500) erreurs.push('Observation : 500 caractères maximum.'); }
    else if (v !== null && v !== undefined && (!Number.isInteger(v) || (v as number) < 0 || (v as number) > 999_999_999_999)) erreurs.push(`${k} : montant entier positif attendu (Ariary).`);
  }
  return { ok: erreurs.length === 0, erreurs };
}

/** Clé d'une saisie financière : « FANOME|PhaGCom ». */
export const cleFinance = (section: SectionFinance, ligne: string) => `${section}|${ligne}`;
